import useHasMounted from '@/hooks/useHasMounted';
import { cn } from '@/utils/utils';
import { type VariantProps, cva } from 'class-variance-authority';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { XIcon } from 'lucide-react';
import { motion } from 'motion/react';
import Balancer from 'react-wrap-balancer';

const notificationStateVersion = 2;

export const notificationAtom = atomWithStorage(
  `notificationState${notificationStateVersion}`,
  true,
);

const notificationVariants = cva(
  'flex flex-col items-left w-full border bg-popover rounded-[10px] group',
  {
    variants: {
      variant: {
        subtle: '',
        loud: '',
      },
    },
    defaultVariants: {
      variant: 'subtle',
    },
  },
);

const contentVariants = cva(
  'gap-1.5 items-center relative overflow-hidden pl-3 pr-2.5 py-2',
  {
    variants: {
      variant: {
        subtle: '',
        loud: 'before:absolute before:inset-0 before:bg-gradient-to-t before:from-badgeLightBlue/30 before:to-transparent',
      },
    },
    defaultVariants: {
      variant: 'subtle',
    },
  },
);

const titleVariants = cva('text-[13px] leading-5 font-medium w-full', {
  variants: {
    variant: {
      subtle: 'text-foreground',
      loud: 'text-badgeBlue font-semibold',
    },
  },
  defaultVariants: {
    variant: 'subtle',
  },
});

const badgeVariants = cva(
  'flex flex-row justify-center items-center rounded-sm bg-badgeLightBlue mb-auto',
  {
    variants: {
      variant: {
        subtle: 'leading-[16px]',
        loud: 'border border-badgeBlue/25 leading-[18px]',
      },
    },
    defaultVariants: {
      variant: 'subtle',
    },
  },
);

const subtitleVariants = cva(
  'text-[13px] leading-5 font-medium relative z-10',
  {
    variants: {
      variant: {
        subtle: 'text-muted-foreground',
        loud: 'text-badgeBlue',
      },
    },
    defaultVariants: {
      variant: 'subtle',
    },
  },
);

interface NotificationProps extends VariantProps<typeof notificationVariants> {
  title: string;
  subtitle: string;
  showNew?: boolean;
  showClose?: boolean;
  underlinedText?: string;
  onClick?: () => void;
}

const Notification = ({
  variant,
  title,
  subtitle,
  showNew,
  showClose,
  underlinedText,
  onClick,
}: NotificationProps) => {
  const [showNotification, setShowNotification] = useAtom(notificationAtom);
  const hasMounted = useHasMounted();

  if (!showNotification || !hasMounted) return null;

  const handleClose = () => {
    setShowNotification(false);
  };

  return (
    <motion.div
      className={cn(
        notificationVariants({ variant }),
        onClick && 'hover:cursor-pointer',
      )}
      onClick={onClick}
    >
      <div className={contentVariants({ variant })}>
        <div className="flex flex-row justify-between items-center pb-0.5 relative z-10">
          <h2 className={titleVariants({ variant })}>
            <Balancer>{title}</Balancer>
          </h2>
          {showNew && (
            <div
              className={cn(
                badgeVariants({ variant }),

                showClose && 'group-hover:hidden',
              )}
            >
              <p className="font-body text-xs  font-semibold text-badgeBlue px-1 tracking-[0.25%]">
                NEW
              </p>
            </div>
          )}
          {showClose && (
            <XIcon
              className="w-5 h-5 p-0.5 text-muted-foreground group-hover:block hidden hover:cursor-pointer hover:bg-popover-hover rounded-sm -mr-1 mb-auto"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
            />
          )}
        </div>
        <p className={subtitleVariants({ variant })}>
          <Balancer>
            {subtitle}{' '}
            {underlinedText && (
              <span className="underline">{underlinedText}</span>
            )}
            .
          </Balancer>
        </p>
      </div>
    </motion.div>
  );
};

export default Notification;
